import React from "react"
import PnlReportingHeader from "../../components/Headers/PnlReportingHeader/Index"
import { getTokenLocal } from "../../../helper/utils"
import { useDispatch } from "react-redux"
import { setLoading, setOpenSidebar } from "../../redux/slices/appSlice"
import {
  setPNLClasses,
  setPNLYears,
  setPnlReportinigTableData,
  setReportingData,
  setTransactionData,
} from "../../redux/slices/pnlReportingSlice"
import PnlReportingTabs from "../../components/Tabs/PnlReportingTabs"
import { Box, Paper } from "@mui/material"
import { useAppSelector } from "../../redux/hooks"
import { PnlReportingState } from "./types"
import PNLTable from "../../components/Table/PNLReportingTable/Index"
import { tableTypes } from "./constants"
import NavigationBreadCrumbs from "./Atom"
import {
  getAvailableYears,
  getClasses,
  getPnlDetailData,
  getPnlReportingData,
  getTransactionDetailData,
} from "./apiUtils"
import { getReportingAggregate } from "./utils"
import { colors } from "../../../styles/commonStyles"
import { fetchGroupsHandler } from "../Huddle/apiUtils"
import { setMaevaGroups } from "../../redux/slices/metricSlice"
import { useNavigate } from "react-router-dom"
import { NavigationAssistant } from "../Huddle/Atom"

const PnlReporting = () => {
  const token: any = getTokenLocal()
  const dispatch = useDispatch()
  dispatch(setOpenSidebar(false))
  const navigate = useNavigate()
  const state: PnlReportingState = useAppSelector((state) => state.pnlReporting)
  const { tableType, selectedLineItem, years, months } = state
  const { selectedTransaction, pnlReportingSelectedClass } = state

  const metricState: any = useAppSelector((store) => store.metric)
  const groups = metricState.maevaSelectedGroupsNoAll
  const setAppLoading = (loadVal: boolean) => dispatch(setLoading(loadVal))

  React.useEffect(() => {
    fetchGroupsHandler({
      token,
      dispatch: (payload: any) => {
        dispatch(setMaevaGroups(payload))
      },
      navigate,
      loading: setAppLoading,
    })
  }, []) // get groups

  React.useEffect(() => {
    if (groups?.length) {
      getAvailableYears({
        token,
        dispatch: (payload: any) => {
          dispatch(setPNLYears(payload))
        },
        navigate,
        loading: setAppLoading,
        group: groups?.[0],
      })
    }
  }, [groups]) // get years with group sk

  React.useEffect(() => {
    if (groups?.length && years?.selected && months?.length) {
      getClasses({
        token,
        dispatch: (payload: any) => {
          dispatch(setPNLClasses(payload))
        },
        navigate,
        loading: setAppLoading,
        group: groups?.[0],
        years: years?.selected?.label,
        months,
      })
    }
  }, [groups, years, months]) // get classes with group sk

  const fetchParams = React.useMemo(
    () => ({
      setLoading: (loadVal: boolean) => dispatch(setLoading(loadVal)),
      token,
      data: {
        lineItem: selectedLineItem,
        tClass: pnlReportingSelectedClass,
        years: years.selected,
        months,
        group: groups?.[0],
        account: selectedTransaction?.transactionAccount,
        accountClass: selectedTransaction?.transactionClass,
        accountSubClass: selectedTransaction?.transactionSubClass,
      },
    }),
    [
      selectedLineItem,
      pnlReportingSelectedClass,
      years.selected,
      months,
      groups,
      selectedTransaction,
      token,
      dispatch,
    ],
  )

  React.useEffect(() => {
    if (pnlReportingSelectedClass && years.selected && months.length) {
      getPnlReportingData({
        dispatch: (payload: any) => {
          dispatch(setPnlReportinigTableData(getReportingAggregate(payload)))
        },
        ...fetchParams,
      })
    }
  }, [pnlReportingSelectedClass, months, years.selected, groups])

  React.useEffect(() => {
    if (
      tableType === tableTypes.DETAIL &&
      selectedLineItem &&
      pnlReportingSelectedClass &&
      years.selected &&
      months.length
    ) {
      getPnlDetailData({
        dispatch: (payload: any) => dispatch(setReportingData(payload)),
        ...fetchParams,
      })
    }
  }, [
    tableType,
    selectedLineItem,
    pnlReportingSelectedClass,
    months,
    years.selected,
    groups,
  ])

  React.useEffect(() => {
    if (
      tableType === tableTypes.SUBDETAIL &&
      selectedLineItem &&
      selectedTransaction?.transactionAccount &&
      pnlReportingSelectedClass &&
      years.selected &&
      months.length
    ) {
      getTransactionDetailData({
        dispatch: (payload: any) => dispatch(setTransactionData(payload)),
        ...fetchParams,
      })
    }
  }, [
    tableType,
    selectedLineItem,
    selectedTransaction,
    pnlReportingSelectedClass,
    months,
    years.selected,
    groups,
  ])

  const navigationData = React.useMemo(
    () => [
      {
        label: "Select a group to proceed",
        value: groups?.length ? true : false,
      },
      {
        label: "Select a year to proceed",
        value: years?.selected ? true : false,
      },
      {
        label: "Select one or more months",
        value: months?.length ? true : false,
      },
      {
        label: "Select a class to proceed",
        value: pnlReportingSelectedClass ? true : false,
      },
    ],
    [groups, years.selected, months, pnlReportingSelectedClass],
  )

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "89vh",
        backgroundColor: colors.background,
      }}
    >
      <PnlReportingHeader name={"P&L Reporting"} />

      {tableType !== tableTypes.SUBDETAIL ? (
        months.length > 0 ? (
          <PnlReportingTabs lineItem={selectedLineItem} />
        ) : (
          false
        )
      ) : (
        <NavigationBreadCrumbs />
      )}
      {pnlReportingSelectedClass ? (
        <Paper
          sx={{
            margin: "0px 20px 0px 20px",
            display: "flex",
            flexGrow: 1,
            flexDirection: "column",
            position: "relative",
          }}
        >
          <PNLTable table={tableType} />
        </Paper>
      ) : (
        <NavigationAssistant data={navigationData} />
      )}
    </Box>
  )
}

export default PnlReporting
